import { Injectable, computed, inject, signal } from '@angular/core';
import { ApiClientService } from '@xbh/common-lib';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { parseYyyyMMddToDate } from '../../pages/common/utils/date-utils';
import { Sa0010MasterDataResponse } from './sa0010.entity';

/**
 * マスタデータグローバルストア（SAP用）
 *
 * アプリ起動時にSA0010APIを呼び出し、全画面で共有するマスタデータを管理する。
 * providedIn: 'root' によりシングルトンとして動作し、ページ遷移後もデータが保持される。
 */
@Injectable({ providedIn: 'root' })
export class MasterDataStore {
  private readonly apiClient = inject(ApiClientService);

  /** マスタデータ本体（未取得時はnull） */
  private readonly _masterData = signal<Sa0010MasterDataResponse['masterData'] | null>(null);

  /** 読み取り専用のマスタデータ */
  readonly masterData = this._masterData.asReadonly();

  /** データ取得済みフラグ */
  readonly isLoaded = computed(() => this._masterData() !== null);

  /**
   * マスタデータを取得してストアに保存する
   * アプリ起動時に一度だけ呼び出す
   */
  load(): Promise<any> {
    return firstValueFrom(
      this.getMasterData().pipe(
        tap((response) => {
          this._masterData.set(response.masterData);
        }),
        catchError(() => {
          // マスタデータ取得失敗時は空データで初期化
          this._masterData.set({
            destinationMaster: [],
            wordMaster: [],
            netAssetValueChartSetting: [],
            codeMaster: [],
            messageMaster: [],
            registrationNumberMaster: [],
          });
          return of(null);
        }),
      ),
    );
  }

  /**
   * 遷移先コードに対応する遷移先の表示タイトルを取得する
   *
   * @param code 遷移先コード
   * @returns 遷移先の表示タイトル（見つからない場合は空文字）
   */
  getDisplayTitle(code: string) {
    return this._masterData()?.destinationMaster?.find((d) => d.transitionDestinationCode === code)?.displayTitle ?? '';
  }

  /**
   * 遷移先コードに対応する遷移先のURLを取得する
   *
   * @param code 遷移先コード
   * @returns 遷移先のURL（見つからない場合は'#'）
   */
  getTransitionDestinationUrl(code: string) {
    return this._masterData()?.destinationMaster?.find((d) => d.transitionDestinationCode === code)?.transitionDestinationUrl ?? '#';
  }

  /**
   * 用語IDに対応する用語名を取得する
   *
   * @param wordId 用語ID
   * @returns 用語名（見つからない場合は空文字）
   */
  getWordName(wordId: string) {
    return this._masterData()?.wordMaster?.find((w) => w.wordId === wordId)?.wordName ?? '';
  }

  /**
   * 用語IDに対応する用語説明を取得する
   *
   * @param wordId 用語ID
   * @returns 用語説明（見つからない場合は空文字）
   */
  getWordExplanation(wordId: string) {
    return this._masterData()?.wordMaster?.find((w) => w.wordId === wordId)?.explanation ?? '';
  }

  /**
   * グラフ設定IDに対応する基準値コードを取得する
   *
   * @param periodCode 期間コード
   * @returns 基準値コード（見つからない場合は空文字）
   */
  getReferenceValueCode(periodCode: string) {
    return this._masterData()?.netAssetValueChartSetting?.find((c) => c.periodCode === periodCode)?.referenceValueCode ?? '';
  }

  /**
   * コード分類に対応するコードマスタ一覧を取得する
   *
   * @param codeClassification コード分類
   * @returns コードマスタリスト（見つからない場合は空配列）
   */
  getCodeMasterByClassification(codeClassification: string) {
    return (
      this._masterData()
        ?.codeMaster?.filter((c) => c.codeClassification === codeClassification)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)) ?? []
    );
  }

  /**
   * コード分類とコード値に対応するコード名称を取得する
   *
   * @param codeClassification コード分類
   * @param codeValue コード値
   * @returns コード名称（見つからない場合は空文字）
   */
  getCodeName(codeClassification: string, codeValue: string) {
    return this._masterData()?.codeMaster?.find((c) => c.codeClassification === codeClassification && c.codeValue === codeValue)?.codeName ?? '';
  }

  /**
   * messageId に対応する messageMainText を取得する
   *
   * @param messageId 検索するメッセージID
   * @returns メッセージ本文（見つからない場合は空文字）
   */
  getMessageText(messageId: string): string {
    const message = this._masterData()?.messageMaster?.find((m) => m.messageId === messageId);
    return message?.messageMainText ?? '';
  }

  /**
   * messageId に対応する message情報 を取得する
   *
   * @param messageId 検索するメッセージID
   * @returns メッセージ情報（見つからない場合はundefined）
   */
  getMessageInfo(messageId: string) {
    const message = this._masterData()?.messageMaster?.find((m) => m.messageId === messageId);
    return message ?? null;
  }

  /**
   * システム日付を取得する
   * <p>
   * マスタ未取得・不正値の場合はブラウザの当日日付を返す。
   * </p>
   *
   * @returns システム日付
   */
  getSystemDate(): Date {
    return parseYyyyMMddToDate(this._masterData()?.systemDate) ?? new Date();
  }

  /**
   * マスタデータ取得API (SA0010) を呼び出す（GET方式）
   */
  getMasterData(): Observable<Sa0010MasterDataResponse> {
    const url = `${environment.bffUrl}/sap/${environment.apiVersion}/api/sa0010`;
    return new Observable((observer) => {
      this.apiClient.request<Sa0010MasterDataResponse>(url, 'get', 'sap', 'sap', '').subscribe({
        next: (data) => {
          observer.next(data);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  /**
   * 登録番号マスタの登録番号を取得する
   * @returns 登録番号（見つからない場合は空文字）
   */
  getRegistrationNo(): string {
    const registrationNumberMaster = this._masterData()?.registrationNumberMaster;
    if (registrationNumberMaster && registrationNumberMaster.length > 0) {
      return registrationNumberMaster[0].registrationNo;
    }
    return '';
  }
}
